import { isWithinDeviceEligibilityWindow, resolveOperatingStatus } from './schedule.js';
import type { LocalDateTimeParts } from './schedule.js';
import { seededShuffle } from './seededRandom.js';
import type { DailyDeviceSelection, OperatingSchedule, PlotDevice } from './types.js';
import { PLOT_DEVICE_SCHEMA_VERSION } from './types.js';
import { SEEDED_PLOT_DEVICE_CATALOG } from './plotDeviceCatalog.js';

export const MAX_DEVICES_PER_IN_WORLD_DAY = 2;

export interface DeviceSelectionInput {
  worldId: string;
  inWorldDate: string;
  selectionSeed: string;
  /** Defaults to midnight UTC on inWorldDate for deterministic tests. */
  selectedAt?: string;
  /** Device ids already used since the start of the in-world week (exclusive of today). */
  usedThisWeek: readonly string[];
  /** World-specific generated devices eligible for selection. */
  worldDevices?: readonly PlotDevice[];
  /** Optional override of the seeded catalog (tests). */
  seededCatalog?: readonly PlotDevice[];
}

export interface DeviceSelectionResult {
  deviceIds: string[];
  daily: DailyDeviceSelection;
}

function weekStartKey(inWorldDate: string): string {
  const d = new Date(`${inWorldDate}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return inWorldDate;
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

/**
 * Merge seeded + world devices preserving catalog order; world entries override
 * same-id seeded rows so generated context wins without duplicate ids.
 */
export function mergePlotDevicePool(
  seededCatalog: readonly PlotDevice[],
  worldDevices: readonly PlotDevice[] = [],
): PlotDevice[] {
  const byId = new Map<string, PlotDevice>();
  for (const device of seededCatalog) {
    byId.set(device.deviceId, device);
  }
  for (const device of worldDevices) {
    byId.set(device.deviceId, device);
  }
  const orderedIds: string[] = [];
  const seen = new Set<string>();
  for (const device of seededCatalog) {
    if (seen.has(device.deviceId)) continue;
    orderedIds.push(device.deviceId);
    seen.add(device.deviceId);
  }
  for (const device of worldDevices) {
    if (seen.has(device.deviceId)) continue;
    orderedIds.push(device.deviceId);
    seen.add(device.deviceId);
  }
  return orderedIds.map((id) => byId.get(id)!);
}

/** Deterministic daily pick: ≤2 devices, none reused later in the same week. */
export function selectDailyPlotDevices(input: DeviceSelectionInput): DeviceSelectionResult {
  const catalog = input.seededCatalog ?? SEEDED_PLOT_DEVICE_CATALOG;
  const pool = mergePlotDevicePool(catalog, input.worldDevices ?? []);
  const excluded = new Set(input.usedThisWeek);
  const eligible = pool.filter((d) => !excluded.has(d.deviceId));
  const shuffleSeed = `${input.selectionSeed}|${input.inWorldDate}|${weekStartKey(input.inWorldDate)}`;
  const shuffled = seededShuffle(
    eligible.map((d) => d.deviceId),
    shuffleSeed,
  );
  const deviceIds = shuffled.slice(0, MAX_DEVICES_PER_IN_WORLD_DAY);
  const selectedAt = input.selectedAt ?? `${input.inWorldDate}T00:00:00.000Z`;

  return {
    deviceIds,
    daily: {
      schemaVersion: PLOT_DEVICE_SCHEMA_VERSION,
      worldId: input.worldId,
      inWorldDate: input.inWorldDate,
      selectionSeed: input.selectionSeed,
      deviceIds,
      selectedAt,
    },
  };
}

/** Accumulate weekly usage after each in-world day selection. */
export function appendWeeklyUsage(
  usedThisWeek: readonly string[],
  selectedDeviceIds: readonly string[],
): string[] {
  const next = new Set(usedThisWeek);
  for (const id of selectedDeviceIds) next.add(id);
  return [...next];
}

/** Whether a device id may be selected again on `inWorldDate` given prior week usage. */
export function isDeviceEligibleThisWeek(
  deviceId: string,
  usedThisWeek: readonly string[],
): boolean {
  return !usedThisWeek.includes(deviceId);
}

/** Closed locations block all device activation; otherwise first/last 10 live minutes only. */
export function isDeviceActivationAllowed(
  schedule: OperatingSchedule,
  local: LocalDateTimeParts,
): boolean {
  if (resolveOperatingStatus(schedule, local).status === 'closed') return false;
  return isWithinDeviceEligibilityWindow(schedule, local);
}
